/**
 * GlobalView — Admin Page (§25.2, Task 1.2)
 *
 * User management panel for admins.
 * - List all users with status
 * - Approve/Reject/Suspend users
 * - Assign roles
 */

import { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { useGlobeStore, DEFAULT_CRT, DEFAULT_NVG, DEFAULT_FLIR } from '../stores/globeStore';
import { setBaseLayerStyle as applyBaseLayerStyleFn } from '../services/baseLayer';
import StatusBadge, { type BadgeVariant } from '../components/ui/StatusBadge';
import { useToastStore } from '../components/ui/Toast';
import { getOpenSkyBrowserStatus } from '../services/layers/AirLayerRenderer';
import type { OpenSkyDirectStatus } from '../services/openSkyDirectClient';
import styles from './AdminPage.module.css';

interface UserRow {
  uid: string;
  email: string;
  displayName: string;
  status: string;
  role: string;
  createdAt: string;
}

type UserRole = 'viewer' | 'analyst' | 'admin';
const ROLE_OPTIONS: UserRole[] = ['viewer', 'analyst', 'admin'];

// ─── FilterSlider (local component) ─────────────────────────────

interface FilterSliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  onChange: (value: number) => void;
}

function FilterSlider({ label, value, min, max, step, defaultValue, onChange }: FilterSliderProps) {
  const isDefault = Math.abs(value - defaultValue) < step * 0.5;
  return (
    <div className={styles.filterSliderRow} id={`filter-slider-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={styles.filterSliderInfo}>
        <span className={styles.filterSliderLabel}>{label}</span>
        <span className={styles.filterSliderValue}>{value.toFixed(2)}</span>
      </div>
      <div className={styles.filterSliderControl}>
        <input
          type="range"
          className={styles.filterSlider}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
        />
        {!isDefault && (
          <button
            className={styles.filterResetButton}
            onClick={() => onChange(defaultValue)}
            title="Zurücksetzen"
          >
            ↺
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminPage({ onClose }: { onClose: () => void }) {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentUser = useAuthStore((s) => s.user);
  const addToast = useToastStore((s) => s.addToast);
  const tileMode = useGlobeStore((s) => s.tileMode);
  const setTileMode = useGlobeStore((s) => s.setTileMode);
  const baseLayerStyle = useGlobeStore((s) => s.baseLayerStyle);
  const setBaseLayerStyleStore = useGlobeStore((s) => s.setBaseLayerStyle);
  const filterSettings = useGlobeStore((s) => s.filterSettings);
  const updateFilterSettings = useGlobeStore((s) => s.updateFilterSettings);

  // ─── OpenSky Settings ─────────────────────────────────────────
  interface ClientInfo {
    label: string;
    clientId: string;
    creditsRemaining: number | null;
    active: boolean;
  }

  interface OpenSkyStatus {
    mode: string;
    dynamicMode: boolean;
    clientCount: number;
    activeClient: { label: string; clientId: string; creditsRemaining: number | null } | null;
    clients: ClientInfo[];
    pollIntervalMs: number;
    minPollIntervalMs: number;
    maxPollIntervalMs: number;
    viewport: { lamin: number; lomin: number; lamax: number; lomax: number } | null;
    entityCount: number;
    lastPollTime: number;
    consecutiveErrors: number;
  }

  const [openSky, setOpenSky] = useState<OpenSkyStatus | null>(null);
  const [browserOpenSky, setBrowserOpenSky] = useState<OpenSkyDirectStatus | null>(null);
  const [intervalValue, setIntervalValue] = useState(10);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<{ users: UserRow[] }>('/admin/users');
      setUsers(response.users || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Laden der Nutzerliste';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Fetch OpenSky settings
  const fetchSettings = useCallback(async () => {
    try {
      const data = await apiClient.get<{ openSky: OpenSkyStatus }>('/admin/settings');
      setOpenSky(data.openSky);
      setIntervalValue(Math.round(data.openSky.pollIntervalMs / 1000));
    } catch {
      // Settings may fail if not admin; ignore
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    const timer = setInterval(() => {
      fetchSettings();
      // Also refresh browser-side status
      setBrowserOpenSky(getOpenSkyBrowserStatus());
    }, 10_000);
    // Initial browser status check
    setBrowserOpenSky(getOpenSkyBrowserStatus());
    return () => clearInterval(timer);
  }, [fetchSettings]);



  const updateUser = async (uid: string, update: { status?: string; role?: string }) => {
    try {
      await apiClient.patch(`/admin/users/${uid}`, update);
      addToast(
        `Nutzer ${update.status ? `Status → ${update.status.toUpperCase()}` : `Rolle → ${update.role?.toUpperCase()}`} aktualisiert`,
        'success'
      );
      fetchUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Aktualisieren';
      addToast(message, 'error');
    }
  };

  const pendingUsers = users.filter((u) => u.status === 'pending');
  const activeUsers = users.filter((u) => u.status === 'active');
  const otherUsers = users.filter((u) => u.status !== 'pending' && u.status !== 'active');

  return (
    <div className={styles.overlay} id="admin-overlay">
      <div className={styles.page} id="admin-page">
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Nutzerverwaltung</h1>
            <span className={styles.subtitle}>Admin-Panel · {users.length} Nutzer</span>
          </div>
          <button
            className={styles.closeButton}
            onClick={onClose}
            id="admin-close-button"
            title="Schliessen"
          >
            ✕
          </button>
        </div>

      {error && (
        <div className={styles.errorBanner}>
          <span>⚠️ {error}</span>
          <button onClick={fetchUsers} className={styles.retryButton}>Wiederholen</button>
        </div>
      )}

      {loading ? (
        <div className={styles.loadingState}>Lade Nutzerliste…</div>
      ) : (
        <>
          {/* System Settings */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              ⚙️ System-Einstellungen
            </h2>

            {/* ── OpenSky Cluster ────────────────────────── */}
            <div className={styles.settingsCard}>
              <div className={styles.cardTitle}>✈️ OpenSky API</div>

              {/* Status row */}
              <div className={styles.settingRow}>
                <div className={styles.settingInfo}>
                  <span className={styles.settingLabel}>Status</span>
                  {browserOpenSky && (
                    <span className={styles.settingHint}>
                      Browser-direkt (eigene IP · kein Cloud Run Egress)
                    </span>
                  )}
                </div>
                <div className={styles.statusPills}>
                  <span className={styles.pill}>
                    {(browserOpenSky?.mode ?? openSky?.mode) === 'oauth2' ? '🔐 OAuth2' : '👤 Anonym'}
                  </span>
                  <span className={styles.pill}>
                    {openSky?.viewport ? '🎯 Viewport' : '🌍 Global'}
                  </span>
                  <span className={styles.pill}>
                    ⏱️ {browserOpenSky
                      ? `${Math.round(browserOpenSky.pollIntervalMs / 1000)}s`
                      : `${intervalValue}s`} (dynamisch)
                  </span>
                  <span className={styles.pill}>
                    ✈️ {(browserOpenSky?.entityCount ?? openSky?.entityCount)?.toLocaleString('de-CH') ?? '—'}
                  </span>
                </div>
              </div>

              {/* Multi-client credits from browser polling */}
              {browserOpenSky && browserOpenSky.clients.length > 0 && (
                <div className={styles.clientList}>
                  {browserOpenSky.clients.map((cl) => (
                    <div key={cl.clientId} className={`${styles.clientRow} ${cl.active ? styles.clientActive : ''}`}>
                      <span className={styles.clientLabel}>{cl.active ? '●' : '○'} {cl.label}</span>
                      <span className={styles.clientCredits}>
                        {cl.creditsRemaining != null
                          ? `${cl.creditsRemaining.toLocaleString('de-CH')} Credits`
                          : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Fallback: backend credits (always — while browser polling not yet active) */}
              {!browserOpenSky && openSky && openSky.clients.length > 0 && (
                <div className={styles.clientList}>
                  {openSky.clients.map((cl) => (
                    <div key={cl.clientId} className={`${styles.clientRow} ${cl.active ? styles.clientActive : ''}`}>
                      <span className={styles.clientLabel}>{cl.active ? '●' : '○'} {cl.label}</span>
                      <span className={styles.clientCredits}>
                        {cl.creditsRemaining != null ? `${cl.creditsRemaining} Credits` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── 3D Tile Mode ───────────────────────────── */}
            <div className={styles.settingsCard}>
              <div className={styles.cardTitle}>🗺️ 3D-Darstellung</div>

              <div className={styles.settingRow}>
                <div className={styles.settingInfo}>
                  <span className={styles.settingLabel}>Tile-Modus</span>
                  <span className={styles.settingHint}>
                    {tileMode === 'google3d'
                      ? 'Google Photorealistic 3D Tiles mit realistischen Gebäuden'
                      : 'Cesium Standard mit OSM Buildings und wählbarem Kartenstil'}
                  </span>
                </div>
                <div className={styles.settingControl}>
                  <div className={styles.tileModeToggleGroup}>
                    <button
                      className={`${styles.modeToggle} ${tileMode === 'google3d' ? styles.modeActive : ''}`}
                      onClick={() => setTileMode('google3d')}
                      id="admin-tile-google"
                    >
                      🌍 Google 3D
                    </button>
                    <button
                      className={`${styles.modeToggle} ${tileMode === 'cesium' ? styles.modeActive : ''}`}
                      onClick={() => setTileMode('cesium')}
                      id="admin-tile-cesium"
                    >
                      🗺️ Standard
                    </button>
                  </div>
                </div>
              </div>

              {/* Base layer style — only for Cesium mode */}
              {tileMode === 'cesium' && (
                <div className={styles.settingRow}>
                  <div className={styles.settingInfo}>
                    <span className={styles.settingLabel}>Kartenstil</span>
                    <span className={styles.settingHint}>Basis-Kartenlayer für den Standard-Modus</span>
                  </div>
                  <div className={styles.settingControl}>
                    <div className={styles.tileModeToggleGroup}>
                      {(['standard', 'satellite', 'dark'] as const).map((s) => (
                        <button
                          key={s}
                          className={`${styles.modeToggle} ${baseLayerStyle === s ? styles.modeActive : ''}`}
                          onClick={() => {
                            setBaseLayerStyleStore(s);
                            const v = useGlobeStore.getState().viewer;
                            if (v && !v.isDestroyed()) {
                              applyBaseLayerStyleFn(v, s);
                            }
                          }}
                          id={`admin-base-style-${s}`}
                        >
                          {s === 'standard' ? 'STD' : s === 'satellite' ? 'SAT' : 'DARK'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Camera Filter Settings ──────────────────── */}
            <div className={styles.settingsCard}>
              <div className={styles.cardTitle}>📷 Kamera-Filter</div>

              {/* CRT */}
              <div className={styles.filterSliderGroup}>
                <div className={styles.filterGroupTitle}>🖥️ CRT — Retro Monitor</div>
                <FilterSlider
                  label="Scanlines"
                  value={filterSettings.crt.scanlineIntensity}
                  min={0} max={0.20} step={0.01}
                  defaultValue={DEFAULT_CRT.scanlineIntensity}
                  onChange={(v) => updateFilterSettings('crt', { scanlineIntensity: v })}
                />
                <FilterSlider
                  label="Rastermass"
                  value={filterSettings.crt.scanlineFrequency}
                  min={200} max={2000} step={50}
                  defaultValue={DEFAULT_CRT.scanlineFrequency}
                  onChange={(v) => updateFilterSettings('crt', { scanlineFrequency: v })}
                />
                <FilterSlider
                  label="Barrel Distortion"
                  value={filterSettings.crt.distortion}
                  min={0} max={0.40} step={0.01}
                  defaultValue={DEFAULT_CRT.distortion}
                  onChange={(v) => updateFilterSettings('crt', { distortion: v })}
                />
                <FilterSlider
                  label="Grünton"
                  value={filterSettings.crt.greenTint}
                  min={0} max={1.0} step={0.05}
                  defaultValue={DEFAULT_CRT.greenTint}
                  onChange={(v) => updateFilterSettings('crt', { greenTint: v })}
                />
                <FilterSlider
                  label="Bloom / Glow"
                  value={filterSettings.crt.bloom}
                  min={0.8} max={1.5} step={0.05}
                  defaultValue={DEFAULT_CRT.bloom}
                  onChange={(v) => updateFilterSettings('crt', { bloom: v })}
                />
                <FilterSlider
                  label="Vignette"
                  value={filterSettings.crt.vignette}
                  min={0} max={4.0} step={0.1}
                  defaultValue={DEFAULT_CRT.vignette}
                  onChange={(v) => updateFilterSettings('crt', { vignette: v })}
                />
              </div>

              {/* NVG */}
              <div className={styles.filterSliderGroup}>
                <div className={styles.filterGroupTitle}>🌙 NVG — Nachtsicht</div>
                <FilterSlider
                  label="Helligkeit"
                  value={filterSettings.nvg.brightness}
                  min={0.5} max={2.5} step={0.1}
                  defaultValue={DEFAULT_NVG.brightness}
                  onChange={(v) => updateFilterSettings('nvg', { brightness: v })}
                />
                <FilterSlider
                  label="Rauschen"
                  value={filterSettings.nvg.noiseIntensity}
                  min={0} max={0.30} step={0.01}
                  defaultValue={DEFAULT_NVG.noiseIntensity}
                  onChange={(v) => updateFilterSettings('nvg', { noiseIntensity: v })}
                />
                <FilterSlider
                  label="Sichtfeld (Röhre)"
                  value={filterSettings.nvg.vignetteSize}
                  min={0.3} max={1.5} step={0.05}
                  defaultValue={DEFAULT_NVG.vignetteSize}
                  onChange={(v) => updateFilterSettings('nvg', { vignetteSize: v })}
                />
                <FilterSlider
                  label="Grünkanal"
                  value={filterSettings.nvg.greenAmount}
                  min={0.5} max={1.0} step={0.05}
                  defaultValue={DEFAULT_NVG.greenAmount}
                  onChange={(v) => updateFilterSettings('nvg', { greenAmount: v })}
                />
                <FilterSlider
                  label="Scanlines"
                  value={filterSettings.nvg.scanlines}
                  min={0} max={0.10} step={0.005}
                  defaultValue={DEFAULT_NVG.scanlines}
                  onChange={(v) => updateFilterSettings('nvg', { scanlines: v })}
                />
              </div>

              {/* FLIR */}
              <div className={styles.filterSliderGroup}>
                <div className={styles.filterGroupTitle}>🔥 FLIR — Wärmebild</div>
                <FilterSlider
                  label="Kontrast"
                  value={filterSettings.flir.contrast}
                  min={1.0} max={2.5} step={0.1}
                  defaultValue={DEFAULT_FLIR.contrast}
                  onChange={(v) => updateFilterSettings('flir', { contrast: v })}
                />
                <FilterSlider
                  label="Vignette"
                  value={filterSettings.flir.vignette}
                  min={0} max={3.0} step={0.1}
                  defaultValue={DEFAULT_FLIR.vignette}
                  onChange={(v) => updateFilterSettings('flir', { vignette: v })}
                />
              </div>
            </div>
          </section>


          {/* Pending Users — prominent */}
          {pendingUsers.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                <StatusBadge variant="pending" pulse />
                Wartend auf Freischaltung ({pendingUsers.length})
              </h2>
              <div className={styles.table}>
                <div className={styles.tableHeader}>
                  <span>Nutzer</span>
                  <span>E-Mail</span>
                  <span>Registriert</span>
                  <span>Aktionen</span>
                </div>
                {pendingUsers.map((user) => (
                  <div key={user.uid} className={styles.tableRow} id={`user-row-${user.uid}`}>
                    <span className={styles.name}>{user.displayName || '—'}</span>
                    <span className={styles.email}>{user.email}</span>
                    <span className={styles.date}>
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString('de-CH') : '—'}
                    </span>
                    <div className={styles.actions}>
                      <button
                        className={styles.approveButton}
                        onClick={() => updateUser(user.uid, { status: 'active', role: 'viewer' })}
                       >
                        ✓ Freischalten
                      </button>
                      <button
                        className={styles.rejectButton}
                        onClick={() => updateUser(user.uid, { status: 'rejected' })}
                      >
                        ✕ Ablehnen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Active Users */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <StatusBadge variant="active" />
              Aktive Nutzer ({activeUsers.length})
            </h2>
            <div className={styles.table}>
              <div className={styles.tableHeader}>
                <span>Nutzer</span>
                <span>E-Mail</span>
                <span>Rolle</span>
                <span>Aktionen</span>
              </div>
              {activeUsers.map((user) => (
                <div key={user.uid} className={styles.tableRow} id={`user-row-${user.uid}`}>
                  <span className={styles.name}>
                    {user.displayName || '—'}
                    {user.uid === currentUser?.uid && (
                      <span className={styles.youBadge}>DU</span>
                    )}
                  </span>
                  <span className={styles.email}>{user.email}</span>
                  <span>
                    <select
                      className={styles.roleSelect}
                      value={user.role}
                      onChange={(e) => updateUser(user.uid, { role: e.target.value })}
                      disabled={user.uid === currentUser?.uid}
                    >
                      {ROLE_OPTIONS.map((role) => (
                        <option key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </option>
                      ))}
                    </select>
                  </span>
                  <div className={styles.actions}>
                    {user.uid !== currentUser?.uid && (
                      <button
                        className={styles.suspendButton}
                        onClick={() => updateUser(user.uid, { status: 'suspended' })}
                      >
                        Sperren
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {activeUsers.length === 0 && (
                <div className={styles.emptyRow}>Keine aktiven Nutzer</div>
              )}
            </div>
          </section>

          {/* Suspended/Rejected Users */}
          {otherUsers.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                Gesperrt / Abgelehnt ({otherUsers.length})
              </h2>
              <div className={styles.table}>
                <div className={styles.tableHeader}>
                  <span>Nutzer</span>
                  <span>E-Mail</span>
                  <span>Status</span>
                  <span>Aktionen</span>
                </div>
                {otherUsers.map((user) => (
                  <div key={user.uid} className={styles.tableRow} id={`user-row-${user.uid}`}>
                    <span className={styles.name}>{user.displayName || '—'}</span>
                    <span className={styles.email}>{user.email}</span>
                    <span>
                      <StatusBadge variant={user.status as BadgeVariant} />
                    </span>
                    <div className={styles.actions}>
                      <button
                        className={styles.approveButton}
                        onClick={() => updateUser(user.uid, { status: 'active', role: 'viewer' })}
                      >
                        Reaktivieren
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
    </div>
  );
}
